import { PencilIcon, UserIcon } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

/*
 * User identity card rendered in the sidebar (and re-used at the top of the
 * account dropdown that opens from it). Pure presentation — the parent
 * decides whether it's used as a button trigger or as menu header content.
 */

interface LoggedUser {
  fullName?: string;
  roleName?: string;
  photoUrl?: string;
}

interface LoggedCardProps {
  user?: LoggedUser | null;
  onEdit?: () => void;
}

function getInitials(name: string | undefined): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((word) => word[0])
    .filter(Boolean)
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export default function LoggedCard({ user, onEdit }: LoggedCardProps) {
  const initials = getInitials(user?.fullName);

  return (
    <div className="flex w-full items-center gap-3">
      <Avatar className="size-10">
        {user?.photoUrl ? (
          <AvatarImage src={user.photoUrl} alt={user.fullName ?? "User"} />
        ) : null}
        <AvatarFallback>
          {user?.fullName ? initials : <UserIcon className="size-4" />}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1 text-left">
        <div className="truncate text-sm font-semibold">
          {user?.fullName ?? ""}
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {user?.roleName ?? ""}
        </div>
      </div>
      {onEdit ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          aria-label="Edit"
        >
          <PencilIcon className="size-4" />
        </Button>
      ) : null}
    </div>
  );
}
